/**
 * The playlist model the engine plays from: the definition rows (PLAN §5.1)
 * materialised into the play order `rundown → segment → part → piece`, with
 * each piece's **effective** fields resolved against its piece type (D22).
 *
 * **Effective piece fields.** A piece row names a `pieceType` and `params`;
 * every other field it does not write comes from the type: `sourceLayer`,
 * `outputLayer`, `lifespan`, `prerollDuration`, `postrollDuration`, and the
 * duration (`enable` unwritten → `{start: 0, duration: type.defaultDuration}`).
 * "Does not write" is read off the raw row (`f` has no such key), because the
 * typed view fills defaults and so cannot tell `part-only` written from
 * `part-only` defaulted. The instance copies these effective fields (§5.2), so
 * playout never reads the type's defaults again.
 *
 * **Problems do not throw.** A part whose fields fail their schema is
 * unplayable with a reason (PLAN §6.4: "the engine marks such a part `invalid`
 * rather than crashing"). A piece with an unknown type or bad params is left
 * out of its part with an error the instance carries, so one typo does not
 * block a take.
 *
 * The model is rebuilt whole from the tree on a row change — O(rows), well
 * inside the budget for PLAN §1.1's 2,000 rows — and is immutable afterwards.
 */
import {
  type AdlibFields,
  type PartFields,
  partFieldsSchema,
  type PieceFields,
  type PlaylistTiming,
  readFields,
  type SegmentFields,
} from '@acp/domain/fields.js';
import type { Row } from '@acp/domain/row.js';
import { childrenOf, ROOT_KEY, type RowTree } from '@acp/domain/tree.js';
import { checkParams } from './params.js';
import type { PieceTypeConfig, ProductionConfig } from './productionConfig.js';

export type PieceDef = {
  readonly pieceId: string;
  readonly partId: string;
  readonly segmentId: string;
  readonly rundownId: string;
  /** Effective fields (module docstring). `params` has the type's defaults filled. */
  readonly fields: PieceFields;
};

export type PieceProblem = { readonly pieceId: string; readonly message: string };

export type PartDef = {
  readonly partId: string;
  readonly segmentId: string;
  readonly rundownId: string;
  readonly fields: PartFields;
  /** Pieces in `ord` order, problems left out. */
  readonly pieces: readonly PieceDef[];
  readonly pieceProblems: readonly PieceProblem[];
  /** Position in the playlist's part order. */
  readonly index: number;
  /** Takeable: not invalid, not floated (Sofie's `isPartPlayable`). */
  readonly playable: boolean;
  readonly invalidReason: string | null;
};

export type SegmentDef = {
  readonly segmentId: string;
  readonly rundownId: string;
  readonly fields: SegmentFields;
  readonly partIds: readonly string[];
  readonly index: number;
};

export type RundownDef = { readonly rundownId: string; readonly segmentIds: readonly string[]; readonly index: number };

export type AdlibDef = {
  readonly adlibId: string;
  readonly rundownId: string;
  readonly fields: AdlibFields;
  /** The fields the row itself writes: those override the piece type's defaults when it is played. */
  readonly written: ReadonlySet<string>;
};

export type PlaylistModel = {
  readonly playlistId: string | null;
  readonly timing: PlaylistTiming;
  readonly rundowns: readonly RundownDef[];
  readonly segments: readonly SegmentDef[];
  readonly parts: readonly PartDef[];
  readonly partById: ReadonlyMap<string, PartDef>;
  readonly segmentById: ReadonlyMap<string, SegmentDef>;
  readonly rundownById: ReadonlyMap<string, RundownDef>;
  readonly adlibById: ReadonlyMap<string, AdlibDef>;
  /**
   * Pieces whose lifespan can outlive their part, in play order — the only
   * candidates the infinite resolution (`infinites.ts`) has to scan.
   */
  readonly infiniteCandidates: readonly PieceDef[];
};

function written(row: Row, field: string): boolean {
  return Object.hasOwn(row.f, field);
}

type EffectiveResult = { readonly ok: true; readonly fields: PieceFields } | { readonly ok: false; readonly message: string };

/** Piece fields with the type's defaults applied where the row wrote nothing (module docstring). */
export function effectivePieceFields(row: Row, fields: PieceFields, type: PieceTypeConfig): EffectiveResult {
  const checked = checkParams(type, fields.params);
  if (!checked.ok) return { ok: false, message: checked.error };
  const out: PieceFields = { ...fields, params: { ...checked.values } };
  if (!written(row, 'sourceLayer')) out.sourceLayer = type.sourceLayer;
  if (!written(row, 'outputLayer')) out.outputLayer = type.outputLayer ?? '';
  if (!written(row, 'lifespan')) out.lifespan = type.lifespan;
  if (!written(row, 'prerollDuration')) out.prerollDuration = type.prerollDuration;
  if (!written(row, 'postrollDuration')) out.postrollDuration = type.postrollDuration;
  if (!written(row, 'enable')) out.enable = { start: 0, duration: type.defaultDuration };
  // A type with no timeline objects is a marker: its pieces are virtual (PLAN §5.1).
  if (!written(row, 'virtual')) out.virtual = type.timeline.length === 0;
  return { ok: true, fields: out };
}

function isInfiniteLifespan(fields: PieceFields): boolean {
  return fields.lifespan !== 'part-only';
}

type Builder = {
  readonly tree: RowTree;
  readonly studio: ProductionConfig;
  readonly segments: SegmentDef[];
  readonly parts: PartDef[];
  readonly infiniteCandidates: PieceDef[];
  readonly adlibs: Map<string, AdlibDef>;
};

function buildPiece(b: Builder, row: Row, part: { partId: string; segmentId: string; rundownId: string }): PieceDef | PieceProblem {
  const read = readFields('piece', row.f);
  if (!read.ok || read.value.kind !== 'piece') return { pieceId: row.rowId, message: `piece fields invalid: ${read.ok ? 'kind' : read.error}` };
  const fields = read.value.fields;
  const type = Object.hasOwn(b.studio.pieceTypes, fields.pieceType) ? b.studio.pieceTypes[fields.pieceType] : undefined;
  if (type === undefined) return { pieceId: row.rowId, message: `unknown piece type "${fields.pieceType}"` };
  const effective = effectivePieceFields(row, fields, type);
  if (!effective.ok) return { pieceId: row.rowId, message: effective.message };
  // A layer the studio does not know would be a timeline layer no resolver accepts (`layerIdSchema`).
  const layer = effective.fields.sourceLayer;
  if (!Object.hasOwn(b.studio.sourceLayers, layer)) return { pieceId: row.rowId, message: `unknown source layer "${layer}"` };
  return { pieceId: row.rowId, partId: part.partId, segmentId: part.segmentId, rundownId: part.rundownId, fields: effective.fields };
}

function buildAdlib(b: Builder, row: Row, rundownId: string): void {
  const read = readFields('adlib', row.f);
  if (read.ok && read.value.kind === 'adlib') {
    b.adlibs.set(row.rowId, { adlibId: row.rowId, rundownId, fields: read.value.fields, written: new Set(Object.keys(row.f)) });
  }
}

function buildPartChildren(b: Builder, row: Row, ids: { partId: string; segmentId: string; rundownId: string }): { pieces: PieceDef[]; pieceProblems: PieceProblem[] } {
  const pieces: PieceDef[] = [];
  const pieceProblems: PieceProblem[] = [];
  for (const child of childrenOf(b.tree, row.rowId)) {
    if (child.kind === 'adlib') buildAdlib(b, child, ids.rundownId);
    if (child.kind !== 'piece') continue;
    const piece = buildPiece(b, child, ids);
    if ('partId' in piece) pieces.push(piece);
    else pieceProblems.push(piece);
  }
  return { pieces, pieceProblems };
}

function buildPart(b: Builder, row: Row, segmentId: string, rundownId: string): void {
  const read = readFields('part', row.f);
  const ids = { partId: row.rowId, segmentId, rundownId };
  const { pieces, pieceProblems } = buildPartChildren(b, row, ids);
  const fields = read.ok && read.value.kind === 'part' ? read.value.fields : null;
  const index = b.parts.length;
  if (fields === null) {
    const invalidReason = read.ok ? 'not a part' : `part fields invalid: ${read.error}`;
    b.parts.push({ ...ids, fields: fallbackPartFields(row), pieces, pieceProblems, index, playable: false, invalidReason });
    return;
  }
  const playable = !fields.invalid && !fields.floated;
  const invalidReason = fields.invalid ? (fields.invalidReason ?? 'invalid') : null;
  b.parts.push({ ...ids, fields, pieces, pieceProblems, index, playable, invalidReason });
  for (const piece of pieces) if (isInfiniteLifespan(piece.fields)) b.infiniteCandidates.push(piece);
}

/** Every part field at its default: parsing `{}` fills them all, so this cannot fail. */
export const DEFAULT_PART_FIELDS: PartFields = partFieldsSchema.parse({});

/** What an unreadable part row is played as: its structure, everything else default, invalid. */
function fallbackPartFields(row: Row): PartFields {
  return { ...DEFAULT_PART_FIELDS, parentId: row.parentId, ord: row.ord, deleted: row.deleted, invalid: true };
}

function buildSegment(b: Builder, row: Row, rundownId: string): string | null {
  const read = readFields('segment', row.f);
  if (!read.ok || read.value.kind !== 'segment') return null;
  const partIds: string[] = [];
  for (const child of childrenOf(b.tree, row.rowId)) {
    if (child.kind !== 'part') continue;
    buildPart(b, child, row.rowId, rundownId);
    partIds.push(child.rowId);
  }
  b.segments.push({ segmentId: row.rowId, rundownId, fields: read.value.fields, partIds, index: b.segments.length });
  return row.rowId;
}

function playlistRows(tree: RowTree, playlistId: string | null): { rundowns: readonly Row[]; timing: PlaylistTiming } {
  const playlist = playlistId === null ? undefined : tree.rows.get(playlistId);
  if (playlist !== undefined && playlist.kind === 'playlist' && !playlist.deleted) {
    const read = readFields('playlist', playlist.f);
    const timing: PlaylistTiming = read.ok && read.value.kind === 'playlist' ? read.value.fields.timing : { type: 'none' };
    return { rundowns: childrenOf(tree, playlist.rowId).filter((r) => r.kind === 'rundown'), timing };
  }
  return { rundowns: childrenOf(tree, ROOT_KEY).filter((r) => r.kind === 'rundown'), timing: { type: 'none' } };
}

/**
 * The model of one playlist (`playlistId`), or of the root rundowns when it is
 * null or not in the tree (a rundown standing alone, PLAN §5.1).
 */
export function buildModel(tree: RowTree, playlistId: string | null, studio: ProductionConfig): PlaylistModel {
  const b: Builder = { tree, studio, segments: [], parts: [], infiniteCandidates: [], adlibs: new Map() };
  const { rundowns: rundownRows, timing } = playlistRows(tree, playlistId);
  const rundowns: RundownDef[] = [];
  for (const rundown of rundownRows) {
    const segmentIds: string[] = [];
    for (const child of childrenOf(tree, rundown.rowId)) {
      if (child.kind === 'adlib') buildAdlib(b, child, rundown.rowId);
      if (child.kind !== 'segment') continue;
      const id = buildSegment(b, child, rundown.rowId);
      if (id !== null) segmentIds.push(id);
    }
    rundowns.push({ rundownId: rundown.rowId, segmentIds, index: rundowns.length });
  }
  return {
    playlistId,
    timing,
    rundowns,
    segments: b.segments,
    parts: b.parts,
    partById: new Map(b.parts.map((p) => [p.partId, p])),
    segmentById: new Map(b.segments.map((s) => [s.segmentId, s])),
    rundownById: new Map(rundowns.map((r) => [r.rundownId, r])),
    adlibById: b.adlibs,
    infiniteCandidates: b.infiniteCandidates,
  };
}
